import React, { useState, useEffect, useMemo } from "react";
import { FaExclamationTriangle, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { TEACHER_JOURNEY_URL, STUENT_JOURNEY_URL } from "../../constants";
import { useSelector, useDispatch } from "react-redux";
import { getStudentMeetings } from "../../redux/slice/student-meeting-slice/student-meeting-thunk";
import { getTeacherMeetings } from "../../redux/slice/teacher-meeting-slice/teacher-meeting-thunk";
import { getMonthStartEndDates } from "../../helper";

const TeacherMeetingAlert = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const teacherMeetings = useSelector((state) =>
    currentUser?.role === "teacher"
      ? state.teacherMeetings
      : state.studentMeetings
  );
  const [visible, setVisible] = useState(true);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Determine base URL based on role
  const baseUrl =
    currentUser?.role === "teacher" ? TEACHER_JOURNEY_URL : STUENT_JOURNEY_URL;

  // Fetch meetings when user or route changes
  useEffect(() => {
    if (currentUser?.id && currentUser?.role) {
      setIsLoading(true);
      const { startDate, endDate } = getMonthStartEndDates(new Date());
      const payload = {
        [currentUser.role === "teacher" ? "teacherId" : "studentId"]:
          currentUser.id,
        startDate,
        endDate,
      };

      const fetchAction =
        currentUser.role === "teacher"
          ? getTeacherMeetings
          : getStudentMeetings;
      dispatch(fetchAction(payload)).finally(() => setIsLoading(false));
    }
  }, [currentUser?.id, currentUser?.role, dispatch]);

  // Process meeting data from Redux store
  const { nextMeeting, totalMeetings, initialTimeLeft } = useMemo(() => {
    const now = new Date();
    const today = now.toISOString().split("T")[0]; // Get YYYY-MM-DD format for today

    const meetings = Array.isArray(teacherMeetings?.data)
      ? teacherMeetings.data
      : [];

    // Convert meeting date and time to Date objects and filter for today
    const upcomingMeetings = meetings
      .map((meeting) => {
        const meetingDateTime = new Date(
          `${meeting.date}T${meeting.startTime}Z`
        );
        return {
          ...meeting,
          time: meetingDateTime,
          completed: meeting.offerSlot?.teacherClickedMeeting || false,
        };
      })
      .filter((meeting) => {
        const isToday = meeting.date === today;
        return isToday && meeting.time > now && !meeting.completed;
      });

    const next = upcomingMeetings.sort((a, b) => a.time - b.time)[0];
    const msLeft = next ? next.time - now : null;

    return {
      nextMeeting: next,
      totalMeetings: upcomingMeetings.length,
      initialTimeLeft: msLeft,
    };
  }, [teacherMeetings.data]);

  // Continuous timer update
  useEffect(() => {
    // Initialize timeLeft with initialTimeLeft from useMemo
    setTimeLeft(initialTimeLeft);

    const interval = setInterval(() => {
      if (nextMeeting) {
        const now = new Date();
        const msLeft = nextMeeting.time - now;
        setTimeLeft(msLeft);
      } else {
        setTimeLeft(null); // Reset timeLeft if no next meeting
      }
    }, 1000);

    return () => clearInterval(interval); // Clean up on unmount or when nextMeeting changes
  }, [nextMeeting, initialTimeLeft]);

  // Calculate progress percentage (100% at 2 hours, 0% at 0 seconds)
  const maxTime = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
  const progressPercentage = timeLeft
    ? Math.max(0, Math.min(100, (timeLeft / maxTime) * 100))
    : 0;

  // Format time left in a descriptive way
  const formatTimeLeft = (ms) => {
    if (!ms || ms <= 0) return "0 minutes";
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));

    const parts = [];
    if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? "s" : ""}`);
    if (minutes > 0 || parts.length === 0)
      parts.push(`${minutes} minute${minutes !== 1 ? "s" : ""}`);

    return parts.join(", ");
  };

  // Determine alert color based on time left
  const getAlertStyle = (ms) => {
    if (!ms || ms <= 0) {
      return { backgroundColor: "#f8d7da", border: "1px solid #f5c6cb" }; // Red
    }
    if (ms <= 15 * 60 * 1000) {
      return { backgroundColor: "#fff3cd", border: "1px solid #ffeeba" }; // Yellow
    }
    if (ms <= 60 * 60 * 1000) {
      return { backgroundColor: "#cce5ff", border: "1px solid #b8daff" }; // Blue
    }
    return { backgroundColor: "#d4edda", border: "1px solid #c3e6cb" }; // Green
  };

  const getProgressColor = (ms) => {
    if (!ms || ms <= 0) return "bg-danger";
    if (ms <= 15 * 60 * 1000) return "bg-warning";
    if (ms <= 60 * 60 * 1000) return "bg-primary";
    return "bg-success";
  };

  const handleRedirect = () => {
    navigate(`${baseUrl}`);
  };

  if (isLoading || !nextMeeting || !visible) return null;

  // Determine name to display based on role
  const displayName =
    currentUser?.role === "teacher"
      ? nextMeeting.student?.fullName
      : nextMeeting.teacher?.fullName;

  return (
    <div className="mt-2 mx-3">
      <div
        className="alert shadow-sm rounded-3 p-3"
        style={{
          fontSize: "0.875rem",
          ...getAlertStyle(timeLeft),
        }}
        role="alert"
      >
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div className="d-flex align-items-center flex-grow-1 me-3">
            <FaExclamationTriangle
              className={`me-2 ${
                timeLeft <= 15 * 60 * 1000 ? "text-warning" : "text-primary"
              } flex-shrink-0`}
            />
            <span className="flex-grow-1">
              You have {totalMeetings} meeting{totalMeetings !== 1 ? "s" : ""}{" "}
              today. Next meeting with{" "}
              <strong>{displayName || "Unknown"}</strong> in{" "}
              {formatTimeLeft(timeLeft)}.
            </span>
            <button
              className="btn btn-sm btn_contained text-white ms-3"
              style={{ padding: "2px 10px" }}
              onClick={handleRedirect}
            >
              View Meetings
            </button>
          </div>

          <button
            type="button"
            className="btn-close flex-shrink-0 ms-2"
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              fontSize: "0.875rem",
              padding: "0.25rem",
            }}
            onClick={() => setVisible(false)}
          >
            <FaTimes />
          </button>
        </div>

        <div className="progress" style={{ height: "8px" }}>
          <div
            className={`progress-bar ${getProgressColor(timeLeft)}`}
            role="progressbar"
            style={{ width: `${progressPercentage}%` }}
            aria-valuenow={progressPercentage}
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>
      </div>
    </div>
  );
};

export default TeacherMeetingAlert;
